import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { encountersAPI } from '../services/api';
import { getProfilePicture, getFirstName, isOnline } from '../utils/helpers';
import { FaHeart, FaMapMarkerAlt, FaRedo, FaHeartBroken, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';

const Matches = () => {
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();

  const logout = async () => {
    try {
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Matches: Logout error:', error);
    }
  };

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const limit = 20; // Sayfa başına eşleşme sayısı

  useEffect(() => {
    fetchMatches(true);
    // Scroll to top (header) when page loads
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, []);

  const fetchMatches = async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setPage(1);
      } else {
        setLoadingMore(true);
      }

      const currentPage = reset ? 1 : page;
      const response = await encountersAPI.getMatches(currentPage, limit);
      console.log('Matches API response:', response);

      const newMatches = response.data || [];
      console.log('New matches count:', newMatches.length);

      if (reset) {
        setMatches(newMatches);
      } else {
        setMatches(prev => [...prev, ...newMatches]);
        setPage(currentPage + 1);
      }

      // Check pagination info
      const pagination = response.pagination;
      if (pagination) {
        setHasMore(pagination.hasMore);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleRefresh = () => {
    console.log('Refresh button clicked - refreshing matches');
    fetchMatches(true);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchMatches(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header title="Matches" />
        <div className="matchus-discovery-loading">
          <div className="matchus-spinner"></div>
          <p>Finding your matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header title="Matches" />

      {/* Main Content */}
      <div className="matchus-main-content">
        <div className="matchus-discovery">
          {/* Page Header - Refresh Button */}
          <div className="matchus-page-header">
            <div className="matchus-page-header-content">
              <button
                type="button"
                className="matchus-refresh-btn"
                onClick={handleRefresh}
                disabled={loading}
              >
                <FaRedo className={loading ? 'animate-spin' : ''} />
                <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>

          {/* Matches Grid */}
          {matches.length === 0 ? (
            <div className="matchus-discovery-empty">
              <div className="matchus-empty-icon">💕</div>
              <h2>No matches yet</h2>
              <p>Keep swiping and liking profiles to find your perfect match!</p>
              <Link
                to="/encounters"
                className="matchus-retry-btn"
              >
                Start Swiping
              </Link>
            </div>
          ) : (
            <>
              <div className="matchus-people-grid">
                {matches.map((match) => (
                  <Link
                    key={match.id}
                    to={`/user/${match.id}`}
                    className="matchus-person-card group"
                  >
                    <div className="matchus-person-image-container">
                      <img
                        className="matchus-person-image"
                        src={getProfilePicture(match)}
                        alt={match.full_name}
                      />

                      {/* Online indicator */}
                      {isOnline(match.last_active) && (
                        <div className="matchus-online-indicator">
                          <div className="matchus-online-dot"></div>
                        </div>
                      )}

                      {/* Match indicator */}
                      <div className="matchus-match-indicator">
                        <FaHeart className="text-white text-sm" />
                      </div>
                    </div>

                    <div className="matchus-person-info">
                      <h3 className="matchus-person-name">
                        {getFirstName(match.full_name)}, {match.age}
                      </h3>
                      <p className="matchus-person-location">
                        <FaMapMarkerAlt className="inline mr-1" />
                        {match.city && `${match.city}`}
                        {match.city && match.country && ', '}
                        {match.country}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="matchus-load-more-container">
                  <button
                    type="button"
                    className="matchus-load-more-btn"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <>
                        <div className="matchus-spinner-small"></div>
                        <span>Loading more matches...</span>
                      </>
                    ) : (
                      <span>Load More Matches</span>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Matches;